'use client';

import { motion } from 'framer-motion';
import type { CSSProperties, ReactNode, RefObject } from 'react';

import { MENU_SURFACE_CLASS } from '../../lib/header-constants';
import type { DropdownPosition } from '../../types/header';
import { cn } from '@/shared/utils/cn';

const DROPDOWN_TRANSITION = {
  bounce: 0,
  duration: 0.24,
  type: 'spring',
} as const;

const DROPDOWN_INITIAL_STATE = { opacity: 0, scale: 0.98, y: -8 };
const DROPDOWN_ANIMATE_STATE = { opacity: 1, scale: 1, y: 0 };
const DROPDOWN_EXIT_STATE = { opacity: 0, scale: 0.98, y: -6 };

interface HeaderDropdownSurfaceProps {
  ariaLabel?: string;
  children: ReactNode;
  className?: string;
  dropdownRef: RefObject<HTMLDivElement | null>;
  position: DropdownPosition;
  variant?: 'div' | 'section';
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const getDropdownSurfaceStyle = (position: DropdownPosition): CSSProperties => ({
  left: `${position.left}px`,
  maxWidth: `${position.maxWidth}px`,
  minWidth: `${position.minWidth}px`,
  top: `${position.top}px`,
  width: position.width ? `${position.width}px` : undefined,
});

const HeaderDropdownSurface = ({
  ariaLabel,
  children,
  className,
  dropdownRef,
  position,
  variant = 'div',
  onMouseEnter,
  onMouseLeave,
}: HeaderDropdownSurfaceProps) => {
  const Surface = variant === 'section' ? motion.section : motion.div;

  return (
    <Surface
      layout
      animate={DROPDOWN_ANIMATE_STATE}
      aria-label={ariaLabel}
      className={cn('global-header-menu-surface fixed origin-top', MENU_SURFACE_CLASS, className)}
      exit={DROPDOWN_EXIT_STATE}
      initial={DROPDOWN_INITIAL_STATE}
      layoutId="global-header-menu-surface"
      ref={dropdownRef}
      style={getDropdownSurfaceStyle(position)}
      transition={DROPDOWN_TRANSITION}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </Surface>
  );
};

export default HeaderDropdownSurface;
